import * as React from 'react';
import { Text } from '@fluentui/react-components';
import type { MessageFormatElement } from 'react-intl';
import { IntlProvider as BaseIntlProvider } from 'react-intl';
import type { Locale } from './locales';
import { isLocaleRTL } from './locales';
import { getMessages } from './messages';
import { getPolyfills } from './polyfills';

export { getLocaleHtml } from './locales';
export type { Locale };

declare global {
  // eslint-disable-next-line @typescript-eslint/no-namespace
  namespace FormatjsIntl {
    interface IntlConfig {
      defaultLocale: 'en';
      fallbackOnEmptyString: true;
      locale: Locale;
    }
  }
}

/* istanbul ignore next */
const LocaleUpdaterContext = React.createContext<(locale: Locale) => void>(() => {});

interface Props {
  children: React.ReactNode;
  locale?: Locale;
}

export function IntlProvider({ children, locale }: Props) {
  const [userLocale, setUserLocale] = React.useState<Locale>(locale || 'en');
  const dir = isLocaleRTL(userLocale) ? 'rtl' : 'ltr';

  React.useEffect(() => {
    const element = document.getElementsByTagName('html')[0] as HTMLElement;
    element.setAttribute('lang', userLocale);
    element.setAttribute('dir', dir);
  }, [dir, userLocale]);

  const updateUserLocale = React.useCallback((locale: Locale | void) => {
    if (locale) {
      setUserLocale(locale);
    }
  }, []);

  const Polyfills = getPolyfills(userLocale);
  const Messages = getMessages(userLocale);

  return (
    <LocaleUpdaterContext.Provider value={updateUserLocale}>
      <Polyfills fallback={<div />}>
        {() => (
          <Messages fallback={<div />}>
            {(
              // @ts-ignore pipelines fails here but not locally
              messages
            ) => (
              <BaseProvider locale={userLocale} messages={'default' in messages ? messages.default : messages}>
                <div dir={dir} lang={locale}>
                  {children}
                </div>
              </BaseProvider>
            )}
          </Messages>
        )}
      </Polyfills>
    </LocaleUpdaterContext.Provider>
  );
}

export function useUpdateLocale() {
  return React.useContext(LocaleUpdaterContext);
}

interface BaseProps {
  children: React.ReactNode;
  locale: Locale;
  messages?: Record<string, string> | Record<string, Array<MessageFormatElement>>;
}

function BaseProvider({ children, locale, messages }: BaseProps) {
  return (
    <BaseIntlProvider
      defaultLocale="en"
      fallbackOnEmptyString
      locale={locale}
      messages={messages}
      // @ts-ignore it's cool
      textComponent={Text}
      wrapRichTextChunksInFragment
    >
      {children}
    </BaseIntlProvider>
  );
}
